import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { DropDownItemValueProps } from 'spindl-shared-components';

import { DatabasePageParams } from '../../components/pages/DatabasePage/types';
import { REFERENCE_DROPDOWN_OPTIONS } from '../constants';
import { MainCellProps, MainModalFieldProps } from '../types';
import { getOptions, getSelectedOption } from '../utils';
import { useEntriesFiltering } from './useEntriesFiltering';

interface SelectFieldProps<T> {
  options?: DropDownItemValueProps[];
  selected: T;
  onDropdownToggle: (show: boolean) => void;
  setSelected: Dispatch<SetStateAction<T>>;
  searchQuery?: string;
  setSearchQuery?: Dispatch<SetStateAction<string | undefined>>;
  totalItemsFound?: number;
  itemsDisplayed?: number;
}

// eslint-disable-next-line @typescript-eslint/comma-dangle
export const useSelectField = <T,>(
  mainProps: MainCellProps | MainModalFieldProps,
  initialValue: T,
): SelectFieldProps<T> => {
  const {
    dataType,
    value,
    getCellSchema,
    getReferenceOptionsData,
    handleDoubleClick,
  } = mainProps;

  const { t } = useTranslation();
  const { databaseSlug } = useParams<DatabasePageParams>();
  const { fieldSchema, fieldName } = getCellSchema?.() || {};
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [loadDefaultReferences, setLoadDefaultReferences] = useState(false);
  const [selected, setSelected] = useState<T>(initialValue);

  const { data: filteredEntries } = useEntriesFiltering(
    databaseSlug,
    fieldSchema?.reference?.tableSlug || '',
    REFERENCE_DROPDOWN_OPTIONS,
    fieldName || '',
    searchQuery,
    undefined,
    !!searchQuery || loadDefaultReferences,
  );

  const onDropdownToggle = (show: boolean) => {
    handleDoubleClick?.();
    setLoadDefaultReferences(show);
  };

  const { referenceFieldNames } = getReferenceOptionsData?.() || {};

  const selectOptions = getOptions(
    value, dataType, fieldSchema, filteredEntries?.items, !!(searchQuery || loadDefaultReferences),
  );

  useEffect(() => {
    if (value !== undefined) {
      const option = getSelectedOption(value, dataType, fieldSchema) || '';
      if (option) {
        setSelected(option as T);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataType, referenceFieldNames, value]);

  return {
    options: selectOptions?.length ? selectOptions : [
      {
        text: {
          value: t('select.no_result'),
        },
        metadata: '',
      },
    ],
    selected,
    onDropdownToggle,
    setSelected,
    searchQuery,
    setSearchQuery,
    totalItemsFound: filteredEntries?.totalItems || 0,
    itemsDisplayed: filteredEntries?.items?.length || 0,
  };
};
